import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BrainCircuit, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import { apiTokenApi, trainingApi } from '../services/api';
import TrainingTabs from '../components/training/TrainingTabs';

/**
 * Who reaches this programme from VS Code and agent sessions (EP-26 FE-16). Training
 * tokens are one TYPE of PMO API token: they are issued on the API Tokens page
 * ("Training programme"), and listed and revoked here -- your own, or all of the
 * programme's for an approver.
 */
type T = Record<string, any>;

export default function TrainingAccessPage() {
  const { programId = '' } = useParams<{ programId: string }>();
  const [tokens, setTokens] = useState<T[]>([]);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [days, setDays] = useState(90);
  const [issued, setIssued] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    trainingApi.trainingTokens(programId).then(setTokens).catch((e) => setMessage(String(e?.message || e)));
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id: string) => {
    try { await trainingApi.revokeTrainingToken(programId, id); load(); } catch (e: any) { setMessage(e?.message || 'Not revoked.'); }
  };
  const issue = async () => {
    setBusy(true); setMessage(''); setIssued(null);
    try {
      setIssued(await apiTokenApi.issueMyTrainingToken(programId, name.trim(), days));
      setName(''); load();
    } catch (e: any) { setMessage(e?.message || 'Not issued.'); } finally { setBusy(false); }
  };
  const live = (t: T) => t.active !== false && (!t.expiresAt || new Date(t.expiresAt) > new Date());

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Model Training</h1>
        <button type="button" onClick={load} className="ml-auto text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>
      <TrainingTabs programId={programId} />
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2 text-sm">
        <h3 className="font-semibold flex items-center gap-1"><KeyRound className="w-4 h-4" /> Working on this programme from VS Code</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Use a <strong>training token</strong>: a PMO API token of the type "Training programme", for one person and one
          machine or agent session. Issue your own below (an admin can also issue one on the{' '}
          <Link to="/admin/api-tokens" className="text-accent hover:underline">API Tokens</Link> page). It reaches only this programme -- its training pages and the work items, comments and links of
          its training project -- acts as that person for viewing and operating work, never approves or changes the profile, and
          stops working when it expires, is revoked, or the person leaves the programme's training groups. Put it in the file
          named by <code>pmo_api_token_file</code> (or <code>PMO_API_TOKEN</code>) with this programme's training project as the project.
        </p>
        {message && <p className="text-xs text-red-600">{message}</p>}
      </section>
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2 text-sm">
        <h3 className="font-semibold">Issue my token</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          For yourself, if you are in one of this programme's training groups. Name it after the machine or agent session
          (one token each), so you can revoke one without the others.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input className="border rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:border-gray-600 w-64"
            placeholder="e.g. laptop VS Code" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="text-xs text-gray-600 dark:text-gray-400">expires in
            <input type="number" min={1} max={180} className="border rounded px-2 py-1 text-xs w-16 mx-1 bg-white dark:bg-gray-800 dark:border-gray-600"
              value={days} onChange={(e) => setDays(Math.max(1, Math.min(180, Number(e.target.value) || 1)))} />days</label>
          <button type="button" disabled={busy || !name.trim()} onClick={issue}
            className="text-xs px-3 py-1 rounded bg-accent text-white disabled:opacity-50">Issue</button>
        </div>
        {issued?.token && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 text-xs space-y-1">
            <p className="font-semibold">Copy it now -- it is shown once and stored only as a hash.</p>
            <code className="block break-all select-all">{issued.token}</code>
            <button type="button" className="text-accent hover:underline" onClick={() => navigator.clipboard?.writeText(issued.token)}>Copy</button>
            <p className="text-gray-600 dark:text-gray-400">Expires {issued.expiresAt ? new Date(issued.expiresAt).toLocaleDateString() : ''}.</p>
          </div>)}
      </section>
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <h3 className="text-sm font-semibold mb-2">Training tokens</h3>
        {!tokens.length && <p className="text-xs text-gray-500">None yet.</p>}
        {tokens.length > 0 && (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th>Owner</th><th>Name</th><th>Expires</th><th>Last used</th><th>State</th><th /></tr></thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t._id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-1">{t.ownerEmail}</td><td>{t.label || t.name}</td>
                  <td>{String(t.expiresAt || '').slice(0, 10)}</td>
                  <td>{t.lastUsed ? String(t.lastUsed).replace('T', ' ').slice(0, 16) : 'never'}</td>
                  <td>{live(t) ? <span className="text-emerald-600">active</span> : <span className="text-gray-500">{t.active === false ? 'revoked' : 'expired'}</span>}</td>
                  <td>{live(t) ? <button type="button" title="Revoke" onClick={() => revoke(t._id)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button> : null}</td>
                </tr>))}
            </tbody>
          </table>)}
      </section>
    </div>
  );
}
